import {FormField} from 'evergreen-ui';
import React from 'react';
import {Observable} from 'rxjs';
import {isAGroupCombo, isTreatmentHourRequired, Referral, ServiceType, TreatmentOffered} from '../../referral';
import {
    CaregiverLocationDisplay,
    maxHoursForOffer,
    OfferHoursDisplayCondition,
    RequiredHourFields
} from '../domain/offer';
import {HoursInput} from './HoursInput';
import {TreatmentOfferedInput} from "./TreatmentOfferedInput";
import {StartDateCaregiverMediatedTreatmentInput} from "./StartDateCaregiverMediatedTreatmentInput";
import {StartDatePractMediatedTreatmentInput} from "./StartDatePractMediatedTreatmentInput";
import {difference, intersection} from "lodash";
import {ServiceLocationTypeInput} from "./ServiceLocationTypeInput";

type TreatmentHourSectionProps = {
    referral: Referral;
    isSubmitting$: Observable<boolean>;
    hoursCond: OfferHoursDisplayCondition;
    locationCond: CaregiverLocationDisplay;
};
export const TreatmentHourSection = ({
                                         referral,
                                         isSubmitting$,
                                         hoursCond,
                                         locationCond,
                                     }: TreatmentHourSectionProps
) => {
    const has3TierABACombinedService = (referral?.serviceTypes ?? []).includes(ServiceType.ThreeTierOrParentTraining) || (referral?.serviceTypes ?? []).includes(ServiceType.ThreeTierOrParentLed);
    if (!hoursCond.isTreatmentHoursRequired) {
        return null;
    }

    const maxHours = maxHoursForOffer(referral);

    return (
        <>
            {
                has3TierABACombinedService &&
                <FormField
                    label='If no, describe your initial service offer and the plan to achieve full services.'/>
            }
            {
                hoursCond.noPresetTreatmentOffered &&
                <TreatmentOfferedInput referral={referral} isSubmitting$={isSubmitting$}/>
            }

            <div className={hoursCond.showHoursInput ? "" : "hidden"}>
                {caredMedicatedHoursRender(hoursCond, locationCond, maxHours, referral, isSubmitting$)}
                {practitionerMedicatedHoursRender(hoursCond, maxHours, referral, isSubmitting$, has3TierABACombinedService)}
            </div>
        </>
    );
};

// const nonMediatedHoursRender = (
//     maxHours: Record<RequiredHourFields, number>,
//     referral: Referral,
//     isSubmitting$: Observable<boolean>) => {
//     return (
//         <div className={"w-1/2"}>
//             {
//                 referral.serviceTypes.includes(ServiceType.Calibrate) &&
//                 <HoursInput maxValue={maxHours['midLevelHours']}
//                             name='midLevelSupervisionHours'
//                             twoDecimalSupport
//                             label='Mid-level Hours:'
//                             width='32em'
//                             isRequired={referral.serviceTypes.includes(ServiceType.Calibrate)}
//                             isSubmitting$={isSubmitting$}
//                 />
//             }
//             <HoursInput maxValue={maxHours['highLevelHours']}
//                         twoDecimalSupport
//                         name='highLevelSupervisionHours'
//                         label='High-level Hours:'
//                         width='32em'
//                         isRequired={true}
//                         isSubmitting$={isSubmitting$}
//             />
//         </div>
//     );
// }
// A three-column grid, the same one the practitioner block below uses. Each Hours field takes
// two columns and its Service Location sits beside it in the third, so the Hours label is what
// says which level a dropdown belongs to. Both dropdowns are labelled Service Location.
// The columns match the practitioner block so the two grids line up when both are on screen.
const caredMedicatedHoursRender = (
    hoursCond: OfferHoursDisplayCondition,
    locationCond: CaregiverLocationDisplay,
    maxHours: Record<RequiredHourFields, number>,
    referral: Referral,
    isSubmitting$: Observable<boolean>) => {
    const show = hoursCond.isIncludeTreatmentOfferedCareMedicated;

    return (
        <div className={show ? "w-full flex" : "hidden"}>
            <div className={'grid grid-cols-3'}>
                <div className={"col-span-2"}>
                    <StartDateCaregiverMediatedTreatmentInput label={"Start Date Caregiver Mediated"}
                                                              referral={referral}
                                                              isRequired={show}
                                                              isSubmitting$={isSubmitting$}/>
                </div>
                {
                    hoursCond.hasCareMediatedMidHours &&
                    <div className={"col-span-2 mr-2"}>
                        <HoursInput maxValue={maxHours['careMediatedMidHours']}
                                    name='caregiverMediatedMidLevelHours'
                                    label='Mid-Level Supervision Hours'
                                    twoDecimalSupport={true}
                                    isRequired={show}
                                    width='32em'
                                    isSubmitting$={isSubmitting$}
                        />
                    </div>
                }
                {
                    locationCond.showMidLevel &&
                    <ServiceLocationTypeInput fieldName={"serviceLocationMidLevelType"}
                                              referral={referral}
                                              isSubmitting$={isSubmitting$}
                                              isRequired={show}
                                              permittedLocations={referral.serviceLocationsMidLevel}/>
                }
                <div className={"col-span-2 mr-2"}>
                    <HoursInput maxValue={maxHours['careMediatedHighHours']}
                                name='caregiverMediatedHighLevelHours'
                                label='High-Level Supervision Hours'
                                twoDecimalSupport={true}
                                isRequired={show}
                                width='32em'
                                isSubmitting$={isSubmitting$}
                    />
                </div>
                {
                    locationCond.showHighLevel &&
                    <ServiceLocationTypeInput fieldName={"serviceLocationHighLevelType"}
                                              referral={referral}
                                              isSubmitting$={isSubmitting$}
                                              isRequired={show}
                                              permittedLocations={referral.serviceLocationsHighLevel}/>
                }
            </div>
        </div>
    );
}

const practitionerMedicatedHoursRender = (
    hoursCond: OfferHoursDisplayCondition,
    maxHours: Record<RequiredHourFields, number>,
    referral: Referral,
    isSubmitting$: Observable<boolean>,
    has3TierABACombinedService: boolean,
) => {
    const show = hoursCond.isTreatmentOfferedPractitioner;
    const hasLocationServiceByLevel = (referral.serviceLocationsParaprofessional && referral.serviceLocationsParaprofessional.length > 0) ||
        (referral.serviceLocationsMidLevel && referral.serviceLocationsMidLevel.length > 0) ||
        (referral.serviceLocationsHighLevel && referral.serviceLocationsHighLevel.length > 0) ||
        (referral.serviceLocationsGroupCare && referral.serviceLocationsGroupCare.length > 0);

    return (
        <div className={show ? "w-full flex" : "hidden"}>
            <div className={'grid grid-cols-3'}>
                {
                    has3TierABACombinedService &&
                    <div className={"col-span-2"}>
                        <StartDatePractMediatedTreatmentInput label={"Start Date Practitioner Mediated"}
                                                              referral={referral}
                                                              isRequired={show}
                                                              isSubmitting$={isSubmitting$}/>
                    </div>
                }
                {
                    // On a caregiver offer this block is hidden with CSS, so its fields stay in
                    // the form. Without the show check the select below registers
                    // serviceLocationType a second time and cancels the required rule set by
                    // the caregiver dropdown.
                    show &&
                    !hasLocationServiceByLevel && referral.serviceLocations.length > 0 &&
                    <div className={has3TierABACombinedService ? '' : 'col-span-3'}>
                        <ServiceLocationTypeInput fieldName={"serviceLocationType"}
                                                  referral={referral}
                                                  isSubmitting$={isSubmitting$}
                                                  isRequired={show}
                                                  permittedLocations={referral.serviceLocations}/>
                    </div>
                }
                {
                    hoursCond.hasDirectHours && hoursCond.hasMidHours && (<>
                        <div className={"col-span-2 mr-2"}>
                            <HoursInput maxValue={maxHours['directCareHours']}
                                        name='directCareHours'
                                        label='Paraprofessional Hours:'
                                        width='32em'
                                        isRequired={show}
                                        twoDecimalSupport
                                        isSubmitting$={isSubmitting$}
                            />
                        </div>
                        {
                            referral.serviceLocationsParaprofessional && referral.serviceLocationsParaprofessional.length > 0 &&
                            <ServiceLocationTypeInput fieldName={"serviceLocationParaprofessionalType"}
                                                      referral={referral}
                                                      isSubmitting$={isSubmitting$}
                                                      isRequired={show}
                                                      permittedLocations={referral.serviceLocationsParaprofessional}/>
                        }
                        <div className={"col-span-2 mr-2"}>
                            <HoursInput maxValue={maxHours['midLevelHours']}
                                        name='midLevelSupervisionHours'
                                        label='Mid-level Supervision Hours:'
                                        width='32em'
                                        isRequired={show}
                                        twoDecimalSupport
                                        isSubmitting$={isSubmitting$}
                            />
                        </div>
                        {
                            referral.serviceLocationsMidLevel && referral.serviceLocationsMidLevel.length > 0 &&
                            <ServiceLocationTypeInput fieldName={"serviceLocationMidLevelType"}
                                                      referral={referral}
                                                      isSubmitting$={isSubmitting$}
                                                      isRequired={show}
                                                      permittedLocations={referral.serviceLocationsMidLevel}/>
                        }
                    </>)
                }
                <div className={"col-span-2"}>
                    <HoursInput maxValue={maxHours['highLevelHours']}
                                name='highLevelSupervisionHours'
                                label='High-level Supervision Hours:'
                                width='32em'
                                isRequired={show}
                                twoDecimalSupport
                                isSubmitting$={isSubmitting$}
                    />
                </div>

                {
                    // Same problem, on serviceLocationHighLevelType.
                    show &&
                    referral.serviceLocationsHighLevel && referral.serviceLocationsHighLevel.length > 0 &&
                    <ServiceLocationTypeInput fieldName={"serviceLocationHighLevelType"}
                                              referral={referral}
                                              isSubmitting$={isSubmitting$}
                                              isRequired={show}
                                              permittedLocations={referral.serviceLocationsHighLevel}/>
                }
                {
                    hoursCond.hasGroupHours &&
                    <>
                        <div className={"col-span-2"}>
                            <HoursInput maxValue={maxHours['groupCareHours']}
                                        name='groupCareHours'
                                        label='Group Care Hours:'
                                        width='32em'
                                        isRequired={show}
                                        twoDecimalSupport
                                        isSubmitting$={isSubmitting$}
                            />
                        </div>
                        {
                            referral.serviceLocationsGroupCare && referral.serviceLocationsGroupCare.length > 0 &&
                            <ServiceLocationTypeInput fieldName={"serviceLocationGroupCareType"}
                                                      referral={referral}
                                                      isSubmitting$={isSubmitting$}
                                                      isRequired={show}
                                                      permittedLocations={referral.serviceLocationsGroupCare}/>
                        }
                    </>
                }
            </div>
        </div>
    );
}

// BHPNPP-1944
export function presetTreatmentOfferedBasedOnServiceTypes(referral: Referral): TreatmentOffered | null {
    if (!isTreatmentHourRequired(referral)) {
        return null;
    }

    const serviceTypes: ServiceType[] = referral?.serviceTypes ?? [];

    // highest priority
    const serviceTypesForAll = [
        [ServiceType.ThreeTierOrParentLed],
        [ServiceType.ThreeTierOrParentTraining]
    ];
    if (serviceTypesForAll.some((sts) => intersection(sts, serviceTypes).length === 1)) {
        // no preset, let users pick
        return null;
    }

    if (isAGroupCombo(serviceTypes)) {
        return null;

        // BHPNPP-1979 revert this for now, and let users pick Treatment Offered
        // to be re-enabled once the Internal app is updated for BHPNPP-1944 group 4
        // return TreatmentOffered.Caregiver_Mediated;
    }

    const serviceTypesForPractitioner = [
        [ServiceType.Traditional_ABA],
        [ServiceType.Social_Skills_Group],
        [ServiceType.Group_ABA],
        [ServiceType.Compass_Behavioral],
        [ServiceType.Traditional_ABA, ServiceType.Social_Skills_Group],
        [ServiceType.Traditional_ABA, ServiceType.Group_ABA],
        [ServiceType.Traditional_ABA, ServiceType.Parent_Led_ABA],
    ];
    if (serviceTypesForPractitioner.some((sts) => difference(sts, serviceTypes).length === 0)) {
        return TreatmentOffered.Practitioner_Mediated;
    }

    return TreatmentOffered.Caregiver_Mediated;
}

// BHPNPP-1944
// group 4
// function groupComboRender(maxHours: Record<RequiredHourFields, number>,
//                           isSubmitting$: Observable<boolean>) {
//     return (
//         <div className="w-1/2">
//             <HoursInput maxValue={maxHours['careMediatedMidHours']}
//                         name='caregiverMediatedMidLevelHours'
//                         label='Mid-Level Supervision Hours'
//                         twoDecimalSupport={true}
//                         isRequired
//                         width='32em'
//                         isSubmitting$={isSubmitting$}
//             />
//             <HoursInput maxValue={maxHours['careMediatedHighHours']}
//                         name='caregiverMediatedHighLevelHours'
//                         label='High-Level Supervision Hours'
//                         twoDecimalSupport={true}
//                         isRequired
//                         width='32em'
//                         isSubmitting$={isSubmitting$}
//             />
//             <HoursInput maxValue={maxHours['groupCareHours']}
//                         name='groupCareHours'
//                         label='Group Care Hours:'
//                         width='32em'
//                         isRequired
//                         twoDecimalSupport
//                         isSubmitting$={isSubmitting$}
//             />
//         </div>
//     );
// }
